'use client'

import { addDays, subDays, subMonths } from 'date-fns'

// ─── Interfaces ─────────────────────────────────────────────────────────────

export interface Room {
  id: string
  name: string
  specialty: string
  capacity: number
  position: number
  isActive: boolean
  schedule: {
    dayOfWeek: number // 0-6 (Sun-Sat)
    openTime: string
    closeTime: string
    isClosed: boolean
  }[]
}

export interface Staff {
  id: string
  name: string
  email: string
  phone: string
  specialties: string[]
  color: string
  isActive: boolean
}

export interface Service {
  id: string
  name: string
  durationMin: number
  bufferMin: number
  price: number
  category: string
  isActive: boolean
  description: string
}

export interface ClientFichaMedica {
  rut: string
  birthDate: string
  gender: string
  address: string
  comuna: string
  cellphone: string
  email: string
  pendingBalance: number
  hta: string // High blood pressure
  dbt: string // Diabetes
  neuropathic: string // Neuropatía
  medications: string
  allergies: string
  neoplasiaHistory: string
  sunReaction: string
  varices: string
  otherComments: string
  freeTrial: string
}

export interface ClientSessionZoneRow {
  date: string
  sessionNum: number
  skinType: string
  hairType: string
  fluency: number
  energy: number
  operator: string
  comments?: string
}

export interface ClientSessionZone {
  zoneName: string // e.g., 'MENTON', 'Axilas'
  rows: ClientSessionZoneRow[]
}

export interface ClientDocument {
  id: string
  title: string
  createdAt: string
  signatureDataUrl?: string
}

export interface ClientPhotoProgress {
  id: string
  appointmentId: string
  appointmentDate: string
  serviceName: string
  photoBeforeUrl: string
  photoAfterUrl: string
  notes: string
}

export interface Client {
  id: string
  firstName: string
  lastName: string
  phone: string
  email: string
  tags: string[]
  avatar: string
  joinedAt: string
  notes: string
  totalVisits: number
  totalSpent: number
  lastVisit: string | null
  ficha?: ClientFichaMedica
  sessionZones?: ClientSessionZone[]
  documents?: ClientDocument[]
  photosProgress?: ClientPhotoProgress[]
}

export interface Appointment {
  id: string
  clientId: string
  clientName: string
  serviceId: string
  service: string
  roomId: string
  room: string
  staffId: string
  staff: string
  start: string
  end: string
  status: 'confirmed' | 'pending' | 'in_progress' | 'completed' | 'cancelled'
  price: number
  phone: string
  notes?: string
}

export interface Package {
  id: string
  name: string
  description: string
  serviceId: string
  serviceName: string
  sessionsTotal: number
  price: number
  validityDays: number
  isActive: boolean
}

export interface ClientPackage {
  id: string
  clientId: string
  clientName: string
  packageId: string
  packageName: string
  sessionsUsed: number
  sessionsLeft: number
  purchasedAt: string
  expiresAt: string
  pricePaid: number
}

export interface GiftCard {
  id: string
  code: string
  amount: number
  balance: number
  buyerName: string
  buyerEmail: string
  recipientName: string
  expiresAt: string
  isActive: boolean
  createdAt: string
}

export interface Payment {
  id: string
  clientId: string
  clientName: string
  type: 'appointment' | 'package' | 'giftcard'
  referenceId: string // appointmentId, clientPackageId, or giftCardId
  referenceName: string // service name, package name, or 'Gift Card'
  amount: number
  method: 'CASH' | 'CARD_TERMINAL' | 'WEBPAY' | 'FLOW' | 'MERCADO_PAGO' | 'BANK_TRANSFER'
  status: 'PAID' | 'PENDING' | 'REFUNDED'
  createdAt: string
}

export interface StockItem {
  id: string
  name: string
  qty: number
  minQty: number
  unit: string
}

export interface CampaignTemplate {
  id: string
  title: string
  content: string
  imageUrl?: string
}

export interface TenantSettings {
  spaName: string
  logoUrl?: string
  timezone: string
  currency: string
  openTime: string
  closeTime: string
  cancellationPolicy: string
  termsUrl?: string
  // New selections:
  warrantyType: 'none' | 'percent' | 'fixed'
  warrantyVal: number // percent value (e.g. 20) or fixed value (e.g. 10000)
  birthdayMessageLeadDays: number // e.g. 0 for exact day, 7 for 7 days before
  birthdayBenefit: string // e.g., "15% de Descuento"
  inactiveThresholdDays: number // e.g. 45 days
}

export interface PersistentNotification {
  id: string
  text: string
  time: string
  unread: boolean
}

// ─── Default Sample Seed Data ────────────────────────────────────────────────

const today = new Date()

const defaultRooms: Room[] = [
  {
    id: 'r-1',
    name: 'Sala Facial',
    specialty: 'facial',
    capacity: 1,
    position: 0,
    isActive: true,
    schedule: Array.from({ length: 7 }, (_, i) => ({
      dayOfWeek: i,
      openTime: '09:00',
      closeTime: '20:00',
      isClosed: i === 0 || i === 6
    }))
  },
  {
    id: 'r-2',
    name: 'Sala Masajes',
    specialty: 'masajes',
    capacity: 1,
    position: 1,
    isActive: true,
    schedule: Array.from({ length: 7 }, (_, i) => ({
      dayOfWeek: i,
      openTime: '09:00',
      closeTime: '20:00',
      isClosed: i === 0
    }))
  },
  {
    id: 'r-3',
    name: 'Sala Uñas',
    specialty: 'uñas',
    capacity: 2,
    position: 2,
    isActive: true,
    schedule: Array.from({ length: 7 }, (_, i) => ({
      dayOfWeek: i,
      openTime: '10:00',
      closeTime: '21:00',
      isClosed: i === 0
    }))
  }
]

const defaultStaff: Staff[] = [
  { id: 'st-1', name: 'Ana María', email: 'anamaria@paitox.cl', phone: '+56911112222', specialties: ['facial', 'depilación'], color: '#06b6d4', isActive: true },
  { id: 'st-2', name: 'Diego Torres', email: 'diego@paitox.cl', phone: '+56922223333', specialties: ['masajes'], color: '#8b5cf6', isActive: true },
  { id: 'st-3', name: 'Camila Vega', email: 'camila@paitox.cl', phone: '+56933334444', specialties: ['uñas'], color: '#ec4899', isActive: true }
]

const defaultServices: Service[] = [
  { id: 'srv-1', name: 'Limpieza Facial Profunda', durationMin: 60, bufferMin: 10, price: 35000, category: 'facial', isActive: true, description: 'Limpieza con extracción, exfoliación química suave y máscara descongestionante.' },
  { id: 'srv-2', name: 'Masaje Piedras Calientes', durationMin: 90, bufferMin: 15, price: 45000, category: 'masajes', isActive: true, description: 'Masaje relajante profundo utilizando piedras volcánicas calientes para aliviar tensión.' },
  { id: 'srv-3', name: 'Esmaltado Permanente', durationMin: 45, bufferMin: 5, price: 20000, category: 'uñas', isActive: true, description: 'Manicura con esmaltado secado en lámpara LED de larga duración.' },
  { id: 'srv-4', name: 'Hidratación Profunda', durationMin: 60, bufferMin: 10, price: 28000, category: 'facial', isActive: true, description: 'Tratamiento de shock hidratante con ácido hialurónico y velo de colágeno.' },
  { id: 'srv-5', name: 'Manicure Spa', durationMin: 45, bufferMin: 5, price: 18000, category: 'uñas', isActive: true, description: 'Exfoliación, hidratación profunda y esmaltado tradicional.' },
  { id: 'srv-6', name: 'Depilación Pierna Completa', durationMin: 40, bufferMin: 10, price: 22000, category: 'depilación', isActive: true, description: 'Depilación con cera hipoalergénica de coco.' },
  { id: 'srv-7', name: 'Masaje Descontracturante', durationMin: 60, bufferMin: 10, price: 38000, category: 'masajes', isActive: true, description: 'Masaje localizado para aliviar dolores musculares severos.' }
]

const sampleFichaMG: ClientFichaMedica = {
  rut: '12.345.678-K',
  birthDate: '1988-11-20',
  gender: 'Femenino',
  address: 'Avenida Providencia 1240',
  comuna: 'Providencia',
  cellphone: '+56912345678',
  email: 'maria.gonzalez@gmail.com',
  pendingBalance: 0,
  hta: 'No',
  dbt: 'No',
  neuropathic: 'No',
  medications: 'Ninguno',
  allergies: 'Alergia suave al látex',
  neoplasiaHistory: 'No',
  sunReaction: 'Tipo III - Piel sensible, bronceado gradual',
  varices: 'Sí, grado leve en pantorrilla',
  otherComments: 'Prefiere sesiones de mañana.',
  freeTrial: 'No'
}

const sampleSessionZonesMG: ClientSessionZone[] = [
  {
    zoneName: 'MENTON',
    rows: [
      { date: '2025-12-09', sessionNum: 1, skinType: 'III', hairType: 'Grueso', fluency: 8, energy: 6, operator: 'Ana María' },
      { date: '2026-01-05', sessionNum: 2, skinType: 'III', hairType: 'Grueso', fluency: 8, energy: 6, operator: 'Ana María', comments: '2 cuadrantes 1 arriba 1 abajo HRLIGHT 45X2' },
      { date: '2026-02-17', sessionNum: 3, skinType: 'III', hairType: 'Grueso', fluency: 9, energy: 6, operator: 'Diego Torres' },
      { date: '2026-04-20', sessionNum: 4, skinType: 'III', hairType: 'Grueso', fluency: 10, energy: 6, operator: 'Ana María', comments: 'HRLIGHT 50X2' }
    ]
  },
  {
    zoneName: 'Axilas',
    rows: [
      { date: '2025-12-09', sessionNum: 1, skinType: 'II', hairType: 'Medio', fluency: 10, energy: 8, operator: 'Ana María' },
      { date: '2026-01-30', sessionNum: 2, skinType: 'II', hairType: 'Medio', fluency: 10, energy: 8, operator: 'Ana María' }
    ]
  }
]

const defaultClients: Client[] = [
  {
    id: 'c-1', firstName: 'María', lastName: 'González', phone: '+56912345678', email: 'maria.gonzalez@gmail.com',
    tags: ['VIP', 'Facial'], avatar: 'MG', joinedAt: subMonths(today, 14).toISOString(), notes: 'Prefiere sesiones de mañana. Piel sensible tipo III.',
    totalVisits: 18, totalSpent: 630000, lastVisit: subDays(today, 3).toISOString(),
    ficha: sampleFichaMG,
    sessionZones: sampleSessionZonesMG,
    documents: [
      { id: 'doc-1', title: 'Consentimiento Limpieza Facial Profunda', createdAt: subMonths(today, 2).toISOString() }
    ],
    photosProgress: [
      { id: 'ph-1', appointmentId: '1', appointmentDate: subDays(today, 3).toLocaleDateString('es-CL'), serviceName: 'Limpieza Facial Profunda', photoBeforeUrl: 'https://images.unsplash.com/photo-1512290923902-8a9f81dc236c?w=150', photoAfterUrl: 'https://images.unsplash.com/photo-1522335789203-aabd1fc54bc9?w=150', notes: 'Piel hidratada con tónico facial' }
    ]
  },
  {
    id: 'c-2', firstName: 'Carolina', lastName: 'Rojas', phone: '+56987654321', email: 'caro.rojas@gmail.com',
    tags: ['Frecuente', 'Masajes'], avatar: 'CR', joinedAt: subMonths(today, 8).toISOString(), notes: 'Alergia a productos con fragancia. Preferencia por aceites naturales.',
    totalVisits: 11, totalSpent: 495000, lastVisit: subDays(today, 1).toISOString(),
    ficha: {
      rut: '18.456.789-2', birthDate: '1995-04-12', gender: 'Femenino', address: 'Las Condes 8500', comuna: 'Las Condes', cellphone: '+56987654321', email: 'caro.rojas@gmail.com', pendingBalance: 0,
      hta: 'No', dbt: 'No', neuropathic: 'No', medications: 'Ninguno', allergies: 'Sin fragancias', neoplasiaHistory: 'No', sunReaction: 'Tipo II', varices: 'No', otherComments: 'Usar aceites neutros.', freeTrial: 'No'
    },
    sessionZones: []
  },
  {
    id: 'c-3', firstName: 'Valentina', lastName: 'Silva', phone: '+56955554444', email: 'valen.silva@outlook.com',
    tags: ['Nueva'], avatar: 'VS', joinedAt: subMonths(today, 2).toISOString(), notes: 'Primera visita referida por María González.',
    totalVisits: 3, totalSpent: 58000, lastVisit: subDays(today, 15).toISOString()
  },
  {
    id: 'c-4', firstName: 'Sofía', lastName: 'Morales', phone: '+56933221100', email: 'sofia.morales@icloud.com',
    tags: ['VIP', 'Uñas', 'Facial'], avatar: 'SM', joinedAt: subMonths(today, 22).toISOString(), notes: 'Cliente desde apertura. Solicitar descuento fidelidad.',
    totalVisits: 31, totalSpent: 1240000, lastVisit: subDays(today, 55).toISOString() // Simulated inactive (> 45 days)
  },
  {
    id: 'c-5', firstName: 'Lucía', lastName: 'Herrera', phone: '+56966778899', email: 'lucia.h@gmail.com',
    tags: ['Frecuente'], avatar: 'LH', joinedAt: subMonths(today, 6).toISOString(), notes: 'Viene siempre con su hermana Javiera.',
    totalVisits: 8, totalSpent: 360000, lastVisit: subDays(today, 10).toISOString()
  }
]

const mkDateStr = (daysOffset: number, h: number, m = 0) => {
  const d = addDays(today, daysOffset)
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), h, m).toISOString()
}

const defaultAppointments: Appointment[] = [
  { id: '1', clientId: 'c-1', clientName: 'María González', serviceId: 'srv-1', service: 'Limpieza Facial Profunda', roomId: 'r-1', room: 'Sala Facial', staffId: 'st-1', staff: 'Ana María', start: mkDateStr(0, 9, 0), end: mkDateStr(0, 10, 0), status: 'confirmed', price: 35000, phone: '+56912345678', notes: 'Prefiere cremas hidratantes suaves' },
  { id: '2', clientId: 'c-2', clientName: 'Carolina Rojas', serviceId: 'srv-2', service: 'Masaje Piedras Calientes', roomId: 'r-2', room: 'Sala Masajes', staffId: 'st-2', staff: 'Diego Torres', start: mkDateStr(0, 10, 30), end: mkDateStr(0, 12, 0), status: 'in_progress', price: 45000, phone: '+56987654321', notes: 'Presión media-alta' },
  { id: '3', clientId: 'c-3', clientName: 'Valentina Silva', serviceId: 'srv-3', service: 'Esmaltado Permanente', roomId: 'r-3', room: 'Sala Uñas', staffId: 'st-3', staff: 'Camila Vega', start: mkDateStr(0, 13, 0), end: mkDateStr(0, 13, 45), status: 'pending', price: 20000, phone: '+56955554444' },
  { id: '4', clientId: 'c-4', clientName: 'Sofía Morales', serviceId: 'srv-4', service: 'Hidratación Profunda', roomId: 'r-1', room: 'Sala Facial', staffId: 'st-1', staff: 'Ana María', start: mkDateStr(0, 14, 0), end: mkDateStr(0, 15, 0), status: 'confirmed', price: 28000, phone: '+56933221100' }
]

const defaultPackages: Package[] = [
  { id: 'pkg-1', name: 'Pack Limpieza Premium', description: 'Tratamiento continuado de 5 sesiones de Limpieza Facial Profunda', serviceId: 'srv-1', serviceName: 'Limpieza Facial Profunda', sessionsTotal: 5, price: 150000, validityDays: 180, isActive: true },
  { id: 'pkg-2', name: 'Suscripción Masajes', description: 'Plan de 10 sesiones de Masaje Descontracturante para bienestar corporal', serviceId: 'srv-7', serviceName: 'Masaje Descontracturante', sessionsTotal: 10, price: 320000, validityDays: 365, isActive: true }
]

const defaultClientPackages: ClientPackage[] = [
  { id: 'cp-1', clientId: 'c-1', clientName: 'María González', packageId: 'pkg-1', packageName: 'Pack Limpieza Premium', sessionsUsed: 2, sessionsLeft: 3, purchasedAt: subMonths(today, 2).toISOString(), expiresAt: addDays(today, 120).toISOString(), pricePaid: 150000 },
  { id: 'cp-2', clientId: 'c-4', clientName: 'Sofía Morales', packageId: 'pkg-2', packageName: 'Suscripción Masajes', sessionsUsed: 5, sessionsLeft: 5, purchasedAt: subMonths(today, 1).toISOString(), expiresAt: addDays(today, 300).toISOString(), pricePaid: 320000 }
]

const defaultGiftCards: GiftCard[] = [
  { id: 'gc-1', code: 'FACIAL50K', amount: 50000, balance: 50000, buyerName: 'Roberto Soto', buyerEmail: 'roberto@gmail.com', recipientName: 'Catalina Soto', expiresAt: addDays(today, 90).toISOString(), isActive: true, createdAt: subDays(today, 5).toISOString() },
  { id: 'gc-2', code: 'SPA100K', amount: 100000, balance: 35000, buyerName: 'María Luz', buyerEmail: 'luz@gmail.com', recipientName: 'Fernanda Lagos', expiresAt: addDays(today, 180).toISOString(), isActive: true, createdAt: subDays(today, 20).toISOString() }
]

const defaultPayments: Payment[] = [
  { id: 'pay-1', clientId: 'c-1', clientName: 'María González', type: 'appointment', referenceId: '1', referenceName: 'Limpieza Facial Profunda', amount: 35000, method: 'WEBPAY', status: 'PAID', createdAt: subDays(today, 3).toISOString() },
  { id: 'pay-2', clientId: 'c-2', clientName: 'Carolina Rojas', type: 'appointment', referenceId: '2', referenceName: 'Masaje Piedras Calientes', amount: 45000, method: 'MERCADO_PAGO', status: 'PAID', createdAt: today.toISOString() },
  { id: 'pay-3', clientId: 'c-1', clientName: 'María González', type: 'package', referenceId: 'cp-1', referenceName: 'Pack Limpieza Premium', amount: 150000, method: 'BANK_TRANSFER', status: 'PAID', createdAt: subMonths(today, 2).toISOString() }
]

const defaultStock: StockItem[] = [
  { id: 'stk-1', name: 'Ampollas Ácido Hialurónico', qty: 15, minQty: 5, unit: 'unidades' },
  { id: 'stk-2', name: 'Aceite Almendras Dulces', qty: 4, minQty: 10, unit: 'litros' }, // Simulated low stock warning
  { id: 'stk-3', name: 'Crema Hidratante Descongestionante', qty: 18, minQty: 4, unit: 'potes' }
]

const defaultCampaignTemplates: CampaignTemplate[] = [
  { id: 't-1', title: 'Oferta Primavera 15%', content: '¡Hola {{name}}! Te extrañamos en el Spa. Disfruta de un 15% de descuento en tu próximo servicio reservando hoy.', imageUrl: 'https://images.unsplash.com/photo-1540555700478-4be289fbecef?w=400' },
  { id: 't-2', title: 'Limpieza Facial Rejuvenecedora', content: '¡Hola {{name}}! Queremos invitarte a probar nuestro nuevo tratamiento facial premium. ¡Reserva tu cupo!', imageUrl: 'https://images.unsplash.com/photo-1512290923902-8a9f81dc236c?w=400' }
]

const defaultNotifications: PersistentNotification[] = [
  { id: 'not-1', text: 'Carolina Rojas agendó Masaje Piedras Calientes', time: 'Hace 5 min', unread: true },
  { id: 'not-2', text: 'Alerta: Stock bajo de Aceite Almendras Dulces (4L de 10L mín)', time: 'Hace 1 hora', unread: true }
]

const defaultSettings: TenantSettings = {
  spaName: 'Innovaderm Spa',
  timezone: 'America/Santiago',
  currency: 'CLP',
  openTime: '09:00',
  closeTime: '21:00',
  cancellationPolicy: 'Permitido cancelar sin costo hasta 24 horas antes del servicio. Cancelaciones posteriores incurren en una penalidad del 50%.',
  warrantyType: 'percent',
  warrantyVal: 20,
  birthdayMessageLeadDays: 0,
  birthdayBenefit: '15% de Descuento en Facial',
  inactiveThresholdDays: 45
}

// ─── Local Storage DB Store Helper ───────────────────────────────────────────

class PersistentDB {
  private get<T>(key: string, defaultValue: T): T {
    if (typeof window === 'undefined') return defaultValue
    const stored = localStorage.getItem(`paitox_db_${key}`)
    if (!stored) {
      this.set(key, defaultValue)
      return defaultValue
    }
    try {
      return JSON.parse(stored) as T
    } catch {
      return defaultValue
    }
  }

  private set<T>(key: string, value: T): void {
    if (typeof window !== 'undefined') {
      localStorage.setItem(`paitox_db_${key}`, JSON.stringify(value))
    }
  }

  // Rooms
  getRooms(): Room[] { return this.get('rooms', defaultRooms) }
  saveRooms(data: Room[]) { this.set('rooms', data) }

  // Staff
  getStaff(): Staff[] { return this.get('staff', defaultStaff) }
  saveStaff(data: Staff[]) { this.set('staff', data) }

  // Services
  getServices(): Service[] { return this.get('services', defaultServices) }
  saveServices(data: Service[]) { this.set('services', data) }

  // Clients
  getClients(): Client[] { return this.get('clients', defaultClients) }
  saveClients(data: Client[]) { this.set('clients', data) }

  // Appointments
  getAppointments(): Appointment[] { return this.get('appointments', defaultAppointments) }
  saveAppointments(data: Appointment[]) { this.set('appointments', data) }

  // Packages
  getPackages(): Package[] { return this.get('packages', defaultPackages) }
  savePackages(data: Package[]) { this.set('packages', data) }

  // Client Packages
  getClientPackages(): ClientPackage[] { return this.get('client_packages', defaultClientPackages) }
  saveClientPackages(data: ClientPackage[]) { this.set('client_packages', data) }

  // Gift Cards
  getGiftCards(): GiftCard[] { return this.get('gift_cards', defaultGiftCards) }
  saveGiftCards(data: GiftCard[]) { this.set('gift_cards', data) }

  // Payments
  getPayments(): Payment[] { return this.get('payments', defaultPayments) }
  savePayments(data: Payment[]) { this.set('payments', data) }

  // Stock
  getStock(): StockItem[] { return this.get('stock', defaultStock) }
  saveStock(data: StockItem[]) { this.set('stock', data) }

  // Campaign Templates
  getCampaignTemplates(): CampaignTemplate[] { return this.get('campaign_templates', defaultCampaignTemplates) }
  saveCampaignTemplates(data: CampaignTemplate[]) { this.set('campaign_templates', data) }

  // Persistent Notifications
  getNotifications(): PersistentNotification[] { return this.get('notifications', defaultNotifications) }
  saveNotifications(data: PersistentNotification[]) { this.set('notifications', data) }

  // Settings
  getSettings(): TenantSettings { return this.get('settings', defaultSettings) }
  saveSettings(data: TenantSettings) { this.set('settings', data) }
}

export const db = new PersistentDB()
